import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

const eventDeletionRequestSchema = z.object({
  status: z.enum(["REQUESTED", "IN_PROGRESS", "SUCCEEDED", "FAILED"]),
  error: z.string().optional(),
  handoffAt: z.unknown().optional(),
});

export type EventDeletionState =
  | { status: "idle" }
  | { status: "submitting" }
  | {
      status: "watching";
      requestStatus: "REQUESTED" | "IN_PROGRESS";
      /**
       * The backend has accepted the request: the event is locked and
       * unpublished, and the removal job has been handed off. From here the
       * admin has nothing left to wait for.
       */
      accepted: boolean;
    }
  | { status: "succeeded" }
  | { status: "failed"; error: string };

/**
 * What one snapshot of `event_deletion_requests/{id}` means for the admin, or
 * null for a document that does not parse.
 *
 * The trigger locks the event, unpublishes it and stamps `handoffAt` on the
 * request in one transaction, while the request is still REQUESTED; the job
 * then moves it to IN_PROGRESS. Either one means the request was accepted.
 */
export function deletionStateFromRequest(
  data: unknown,
): EventDeletionState | null {
  const parsed = eventDeletionRequestSchema.safeParse(data);
  if (!parsed.success) return null;
  const { status, error, handoffAt } = parsed.data;
  if (status === "SUCCEEDED") return { status: "succeeded" };
  if (status === "FAILED") {
    return { status: "failed", error: error ?? "Event deletion failed" };
  }
  return {
    status: "watching",
    requestStatus: status,
    accepted: status === "IN_PROGRESS" || handoffAt != null,
  };
}

export function eventDeletionRequestDocument(
  eventId: string,
  uid: string,
  requestedAt: unknown,
) {
  return {
    type: "DELETE_EVENT",
    eventId,
    requestedBy: uid,
    requestedAt,
    status: "REQUESTED",
  } as const;
}

export function useEventDeletion() {
  const [state, setState] = useState<EventDeletionState>({ status: "idle" });
  const unsubscribe = useRef<(() => void) | null>(null);

  useEffect(() => () => unsubscribe.current?.(), []);

  const submit = useCallback(async (eventId: string, uid: string) => {
    unsubscribe.current?.();
    unsubscribe.current = null;
    setState({ status: "submitting" });
    try {
      const request = await addDoc(
        collection(db, "event_deletion_requests"),
        eventDeletionRequestDocument(eventId, uid, serverTimestamp()),
      );
      setState({
        status: "watching",
        requestStatus: "REQUESTED",
        accepted: false,
      });
      const stop = onSnapshot(
        doc(db, "event_deletion_requests", request.id),
        (snapshot) => {
          if (!snapshot.exists()) return;
          const next = deletionStateFromRequest(snapshot.data());
          if (next === null) return;
          if (next.status === "succeeded" || next.status === "failed") {
            stop();
            unsubscribe.current = null;
          }
          setState(next);
        },
        (error) => {
          stop();
          unsubscribe.current = null;
          setState({ status: "failed", error: error.message });
        },
      );
      unsubscribe.current = stop;
    } catch (error) {
      setState({
        status: "failed",
        error:
          error instanceof Error
            ? error.message
            : "Could not request event deletion",
      });
    }
  }, []);

  const reset = useCallback(() => setState({ status: "idle" }), []);
  return { state, submit, reset };
}
